// Post-approval handling for the PayPal button, kept free of Alpine/Livewire imports so it can be
// unit tested with node --test.
//
// The rule this module exists to enforce: once PayPal has approved, the donor is never told to
// "try again". Every failure past that point (a lost capture response, a blocked top-window
// redirect) can happen after the money has already moved, and a retry mints a fresh intent and a
// second charge. Donors who saw "Please try again" after a successful capture and paid twice were
// the remaining duplicate-charge source after EFG130/EFG172.

export const APPROVAL_COMPLETED = 'completed'
export const APPROVAL_UNCONFIRMED = 'unconfirmed'
export const APPROVAL_RESTARTED = 'restarted'

/**
 * Send the top window (the host page around the donation embed iframe) to the thank-you page.
 *
 * A sandboxed or cross-origin host can refuse top navigation, and assigning window.top.location
 * then throws. There is deliberately no fallback that navigates the iframe itself: that would
 * replace the completed message and its target=_top link, and a thank-you page that refuses
 * framing would leave the donor a broken frame they are likely to reload into a fresh intent.
 * Returns false when the navigation was refused; the caller is already showing the completed
 * state with a manual link, so that is not an error to the donor.
 */
export function navigateAfterApproval(url, topWindow) {
    try {
        topWindow.location.href = url

        return true
    } catch (error) {
        console.error('PayPal approval redirect was refused', error)

        return false
    }
}

/**
 * Run the one-time capture and settle the button into a terminal state.
 *
 * capture() resolves with the server's return value. The server never throws for a failed capture
 * (embed.blade.php::capturePaypalOrder() turns every exception into a string), so a rejected
 * promise means the response was lost, not that the charge failed: the outcome is unknown and
 * must not invite a retry.
 */
export async function handleOneTimeApproval({ capture, restart, complete, unconfirmed }) {
    let returnValue

    try {
        returnValue = await capture()
    } catch (error) {
        console.error('PayPal capture response was lost', error)
        unconfirmed()

        return APPROVAL_UNCONFIRMED
    }

    // A declined funding source is the one case PayPal itself asks us to retry, inside the same
    // order, so it cannot double-charge.
    if (returnValue === 'INSTRUMENT_DECLINED') {
        await restart()

        return APPROVAL_RESTARTED
    }

    complete()

    return APPROVAL_COMPLETED
}

/**
 * Build the PayPal Buttons onApprove callback for one rendered button.
 *
 * One-time donations capture first; subscriptions are already active once PayPal approves, so
 * they complete straight away. Neither branch has a retry error path, by design.
 *
 * @param {object} options
 * @param {string|null} options.frequency  'one_time' captures; anything else is a subscription.
 * @param {string} options.pmGateway       'paypal_wofi' carries the intention id to its redirect.
 * @param {string} options.redirect        Thank-you URL.
 * @param {string|number} options.intentionId
 * @param {() => Promise<string>} options.capture  Server capture call for one-time donations.
 * @param {(url: string) => void} options.complete
 * @param {() => void} options.unconfirmed
 */
export function buildOnApprove({ frequency, pmGateway, redirect, intentionId, capture, complete, unconfirmed }) {
    const completedUrl = pmGateway === 'paypal_wofi'
        ? `${redirect}&intention_id=${intentionId}`
        : redirect

    return async (data, actions) => {
        if (frequency === 'one_time') {
            return handleOneTimeApproval({
                capture,
                restart: () => actions.restart(),
                complete: () => complete(redirect),
                unconfirmed,
            })
        }

        complete(completedUrl)

        return APPROVAL_COMPLETED
    }
}
